import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Search, MoreHorizontal, Pin, RotateCcw, BookOpen, GraduationCap,
  FlaskConical, Brain, FileText, SlidersHorizontal, Calculator,
  Atom, History, Globe, Star
} from "lucide-react";
import { EDUCATIONAL_LEVELS } from "../../utils/materialTaxonomy";

// Academic subject icons using Lucide
const floatingIcons = [
  { icon: Atom, label: "Physics", size: 52, pos: "top-[10%] left-[6%]", color: "text-blue-400" },
  { icon: Calculator, label: "Mathematics", size: 44, pos: "top-[18%] right-[8%]", color: "text-emerald-400" },
  { icon: History, label: "History", size: 48, pos: "bottom-[28%] left-[12%]", color: "text-amber-400" },
  { icon: Globe, label: "Geography", size: 50, pos: "top-[35%] right-[18%]", color: "text-cyan-400" },
  { icon: Brain, label: "CS", size: 42, pos: "bottom-[20%] right-[6%]", color: "text-indigo-400" },
  { icon: FlaskConical, label: "Chemistry", size: 46, pos: "top-[55%] left-[4%]", color: "text-pink-400" },
  { icon: BookOpen, label: "Literature", size: 50, pos: "top-[8%] left-[28%]", color: "text-emerald-500" },
  { icon: GraduationCap, label: "Biology", size: 44, pos: "bottom-[15%] left-[32%]", color: "text-green-400" },
];

const typeOptions = [
  { id: "all", label: "All", icon: BookOpen },
  { id: "lectures", label: "Lectures", icon: GraduationCap },
  { id: "exercises", label: "Exercises", icon: FileText },
  { id: "courses", label: "Courses", icon: Brain },
  { id: "quizzes", label: "Quizzes", icon: FlaskConical },
];

const sourceOptions = [
  { id: "all", label: "All" },
  { id: "my-uploads", label: "My Uploads" },
  { id: "my-saved", label: "My Saved" },
  { id: "universities", label: "Universities" },
  { id: "uni-prep", label: "University Prep" },
  { id: "companies", label: "Companies" },
  { id: "individuals", label: "Individuals" },
];

const yearOptions = ["All", "2026", "2025", "2024", "2023", "2022", "2021", "2020"];

const levelOptions = ["All", ...EDUCATIONAL_LEVELS];

const sortOptions = [
  { id: "newest", label: "Newest" },
  { id: "popular", label: "Most popular" },
  { id: "highest-rated", label: "Highest rated" },
];

const Slider = ({ value, onValueChange, min, max, step }) => {
  const handleChange = (e) => {
    onValueChange([parseFloat(e.target.value)]);
  };

  return (
    <div className="w-full h-8 flex items-center relative group/slider">
      {/* Background Track */}
      <div className="absolute w-full h-1 bg-muted/20 rounded-full overflow-hidden">
        <div 
          className="h-full bg-accent/40 shadow-[0_0_10px_rgba(16,185,129,0.2)]" 
          style={{ width: `${((value[0] - min) / (max - min)) * 100}%` }}
        />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={handleChange}
        className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer z-10
          [&::-webkit-slider-thumb]:appearance-none 
          [&::-webkit-slider-thumb]:w-4 
          [&::-webkit-slider-thumb]:h-4 
          [&::-webkit-slider-thumb]:rounded-full 
          [&::-webkit-slider-thumb]:bg-muted/30 
          [&::-webkit-slider-thumb]:shadow-[0_0_15px_rgba(0,0,0,0.5),0_0_5px_rgba(var(--accent),0.8)]
          [&::-webkit-slider-thumb]:border-2 
          [&::-webkit-slider-thumb]:border-accent
          [&::-webkit-slider-thumb]:transition-all
          [&::-webkit-slider-thumb]:duration-300
          [&::-webkit-slider-thumb]:hover:scale-125
          [&::-webkit-slider-thumb]:active:scale-95"
      />
    </div>
  );
};

const LibraryHero = ({ query, onQueryChange, filters, onFiltersChange, sortBy, onSortChange, totalResults, scrollContainerRef, showFilters, setShowFilters }) => {
  const [isFocused, setIsFocused] = useState(false);
  const heroRef = useRef(null);

  const toggleMulti = (key, val, allVal) => {
    const current = filters[key];
    if (val === allVal) {
      onFiltersChange({ ...filters, [key]: [allVal] });
      return;
    }
    const without = current.filter(v => v !== allVal);
    const next = without.includes(val) ? without.filter(v => v !== val) : [...without, val];
    onFiltersChange({ ...filters, [key]: next.length === 0 ? [allVal] : next });
  };

  const resetFilters = () => {
    onFiltersChange({ types: ["all"], sources: ["all"], years: ["All"], levels: ["All"], minRating: 0 });
    onSortChange("newest");
  };

  return (
    <div id="library-hero-top" className="relative w-full z-10">
      {/* ───── Full-width hero ───── */}
      <div 
        ref={heroRef} 
        className="relative w-full min-h-screen flex flex-col items-center justify-center bg-transparent"
      >
        {/* Background Clipping Container - Restricts decorative elements without clipping UI */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Decorative elements */}
          <div className="absolute inset-x-0 top-0 h-screen bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.12)_0%,transparent_70%)]" />
          
          {/* Smooth transition to Archive section */}
          <div className="absolute inset-x-0 bottom-0 h-[40vh] bg-gradient-to-t from-background to-transparent z-0" />

          {/* Floating icons layer */}
          {/* <div className="absolute inset-0">
            {floatingIcons.map((item, idx) => (
              <div 
                key={idx} 
                className={`absolute ${item.pos} opacity-20 hover:opacity-100 transition-all duration-700 cursor-default group/icon`}
              >
                <div className="relative">
                  <item.icon 
                    size={item.size} 
                    className={`${item.color} filter blur-[0.5px] group-hover/icon:blur-0 transition-all duration-500`} 
                  />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 rounded bg-accent/90 text-[10px] text-accent-foreground opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap">
                    {item.label}
                  </span>
                </div>
              </div>
            ))}
          </div> */}
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-8 text-center -mt-64">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-nav-foreground mb-4 tracking-tight leading-tight">
            Discover the Library
          </h1>
          <p className="text-nav-foreground/70 text-xs md:text-sm max-w-lg mx-auto mb-8 font-medium leading-relaxed">
            Search through books, notes, courses, and academic resources<br className="hidden md:block" />from across universities and experts.
          </p>

          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => {
                const lang = window.location.pathname.split('/')[1] || 'en';
                window.location.href = `/${lang}/notes`;
              }}
              className="px-6 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 font-bold text-xs hover:bg-primary/20 transition-all flex items-center gap-2"
            >
              <FileText size={14} />
              Go to My Notes
            </button>
          </div>

          <div className="relative max-w-3xl mx-auto">
            <div className={`relative flex items-center h-12 rounded-xl border transition-all duration-500 ${isFocused ? 'border-primary/40 bg-muted shadow-[0_0_50px_rgba(var(--primary),0.15)]' : 'border-border bg-card/50 hover:bg-card hover:border-border/80'}`}>
              <Search className={`absolute left-5 w-4 h-4 transition-colors duration-500 hidden sm:block ${isFocused ? 'text-primary' : 'text-muted-foreground'}`} />
              <input
                id="hero-search-input"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Search resources..."
                className="flex-1 pl-6 sm:pl-14 pr-4 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none font-medium h-full"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`h-9 px-3 mr-1.5 rounded-lg flex items-center gap-2 transition-all duration-300 active:scale-95 font-bold text-[10px] uppercase tracking-wider ${showFilters ? 'bg-primary text-primary-foreground shadow-lg' : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'}`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Filters</span>
              </button>
            </div>

            {/* Filters shelf */}
            {showFilters && (
              <div className="absolute top-full left-0 right-0 mt-2 p-8 rounded-xl bg-card border border-border shadow-[0_40px_80px_rgba(0,0,0,0.5)] text-left animate-in fade-in slide-in-from-top-4 duration-500 z-[101] max-w-3xl mx-auto">
                <div className="relative z-10 space-y-4">
                    <div className="flex items-start">
                    <label className="w-24 pt-1 text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">Type</label>
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {typeOptions.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => toggleMulti("types", opt.id, "all")}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all duration-300 ${filters.types.includes(opt.id) ? 'bg-primary/10 border-primary/40 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]' : 'bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`}
                        >
                          <opt.icon size={12} className={filters.types.includes(opt.id) ? 'opacity-100' : 'opacity-40'} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border/30 w-full" />

                    <div className="flex items-start">
                    <label className="w-24 pt-1 text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">Level</label>
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {levelOptions.map(lvl => (
                        <button
                          key={lvl}
                          onClick={() => toggleMulti("levels", lvl, "All")}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all duration-300 ${filters.levels.includes(lvl) ? 'bg-primary/10 border-primary/40 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]' : 'bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`}
                        >
                          {lvl}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border/30 w-full" />

                    <div className="flex items-start">
                    <label className="w-24 pt-1 text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">Sources</label>
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {sourceOptions.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => toggleMulti("sources", opt.id, "all")}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all duration-300 ${filters.sources.includes(opt.id) ? 'bg-primary/10 border-primary/40 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]' : 'bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border/30 w-full" />

                    <div className="flex items-start">
                    <label className="w-24 pt-1 text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">Years</label>
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {yearOptions.map(year => (
                        <button
                          key={year}
                          onClick={() => toggleMulti("years", year, "All")}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all duration-300 ${filters.years.includes(year) ? 'bg-primary/10 border-primary/40 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]' : 'bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border/30 w-full" />

                    <div className="flex items-center">
                    <label className="w-24 text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">Rating</label>
                    <div className="flex-1 flex items-center gap-6 pr-4">
                        <div className="flex-1">
                          <Slider
                            value={[filters.minRating]}
                            onValueChange={([v]) => onFiltersChange({ ...filters, minRating: v })}
                            min={0}
                            max={10}
                            step={0.5}
                          />
                        </div>
                        <div className="flex items-center gap-2 group/rate transition-all duration-300">
                          <span className="text-lg font-display font-black text-primary tracking-tighter">{filters.minRating === 0 ? "0.0" : filters.minRating.toFixed(1)}</span>
                          <Star size={16} className={`transition-all duration-500 ${filters.minRating > 0 ? 'text-primary fill-primary scale-110' : 'text-muted-foreground/20'}`} />
                        </div>
                    </div>
                  </div>
                  <div className="h-px bg-border/30 w-full" />

                  {/* SORT */}
                  <div className="flex items-start">
                    <label className="w-32 pt-2 text-[11px] font-black text-muted-foreground uppercase tracking-[0.2em]">Sort</label>
                    <div className="flex-1 flex flex-wrap gap-3">
                      {sortOptions.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => onSortChange(opt.id)}
                          className={`px-5 py-2.5 rounded-xl text-xs font-bold border transition-all duration-300 ${sortBy === opt.id ? 'bg-primary/10 border-primary/40 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]' : 'bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-border/30 w-full" />

                  {/* Footer shelf redone */}
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-[0.1em]">
                      {totalResults} results
                    </span>
                    <button onClick={resetFilters} className="text-sm font-bold text-muted-foreground/60 hover:text-foreground transition-all flex items-center gap-2">
                      <RotateCcw size={16} /> Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scroll Indicator matching reference (Floating mouse) */}

        {/* Scroll Indicator matching reference (Floating mouse) */}
        <div 
          className="absolute bottom-32 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-1 cursor-pointer group pointer-events-auto"
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight - 100,
              behavior: "smooth"
            });
          }}
        >
          <span className="text-[10px] font-black text-nav-foreground/40 uppercase tracking-[0.4em] group-hover:text-nav-foreground group-hover:opacity-100 transition-all duration-500">Explore</span>
          <div className="w-7 h-11 rounded-full border-2 border-nav-foreground/20 flex justify-center p-2 transition-all duration-500 group-hover:border-nav-foreground/60 group-hover:shadow-[0_0_20px_hsl(var(--nav-foreground)/0.1)]">
            <div className="w-1 h-2 bg-nav-foreground rounded-full animate-bounce-slow" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryHero;
